import { Component, Input, OnChanges } from '@angular/core';
import { TranslationService } from '@scripts/woo_services.module/TranslationService';
import { ModalContent } from '@src/shared/modal/confirm-modal/confirm-modal.type';
import { ModalService } from '@src/shared/modal/modal.service';
import { uniq } from '../../../utils/array';
import { SimpleChanges } from '../../../utils/types';
import {
  AttestStatus,
  Invoice,
  InvoiceService,
  InvoiceStatus,
  ManualInvoice,
} from '../../../woo_services.module/InvoiceService';
import { Campaign } from '../../../woo_services.module/shared-types';
import { InvoiceType } from './invoice-form.component';

@Component({
  selector: 'invoices',
  templateUrl: './invoices.component.html',
  styleUrls: ['./invoices.component.scss'],
  standalone: false,
})
export class Invoices implements OnChanges {
  readonly InvoiceType = InvoiceType;
  readonly InvoiceStatus = InvoiceStatus;

  @Input() campaign: Campaign;

  invoicesDisabled: boolean;
  invoiceType = InvoiceType.Credit;

  show = {
    invoiceForm: false,
    adminPanel: false,
    loadingInvoices: false,
  };

  invoiceSums = {
    sent_total_gross: 0,
    sent_total_vat: 0,
    unsent_total_gross: 0,
    unsent_total_vat: 0,
  };

  invoices = [] as Invoice[];

  constructor(
    private invoiceService: InvoiceService,
    private modalService: ModalService,
    private translationService: TranslationService,
  ) {}

  ngOnChanges(changes: SimpleChanges<Invoices>): void {
    if (changes.campaign && this.campaign) {
      this.reloadInvoices();
      this.invoicesDisabled = this.campaign.targetings.every((t) => !t.send_invoices);
    }
  }

  private validateInvoicePlan(diff: number): boolean {
    const campaign = this.campaign;
    const expectedBudget = campaign.new_order_value || campaign.order_value;
    let totalInvoiceBudget = this.invoices
      .map((invoice) => (invoice.status === 'cancelled' ? 0 : parseFloat(invoice.total_gross)))
      .reduce((sum, value) => sum + Number(value), 0);
    totalInvoiceBudget = totalInvoiceBudget + diff;
    return Math.abs(expectedBudget - totalInvoiceBudget) < 1;
  }

  cancelInvoice(invoice: Invoice): void {
    const dialogContent: ModalContent = {
      title: 'Makulera faktura',
      texts: [`Det här går inte att ångra så tänk gärna till en extra gång.`],
      confirmLabel: 'Makulera',
      cancelLabel: 'Avbryt',
    };
    const valid = this.validateInvoicePlan(-invoice.total_gross);
    if (!valid) {
      dialogContent.warnings = [
        {
          title: '',
          texts: [
            'Observera att faktureringsplanen inte är lika med kampanjens totala budget om du makulerar denna fakturan.',
          ],
        },
      ];
    }
    this.modalService.openConfirm(dialogContent).then((response) => {
      if (!response.confirm) return;

      this.invoiceService.cancelInvoice(invoice.id).then(() => {
        this.modalService.openSuccess('Fakturan har makulerats.').then(() => {
          this.reloadInvoices();
        });
      });
    });
  }

  newInvoice(type: InvoiceType): void {
    this.invoiceType = type;
    this.show.invoiceForm = true;
  }

  closeInvoiceForm(): void {
    this.show.invoiceForm = false;
  }

  private reloadInvoices(): void {
    this.show.loadingInvoices = true;
    this.invoiceService.getInvoicesForCampaign(this.campaign.id).then((invoices) => {
      this.invoices = invoices;
      this.summariseInvoices();
      this.show.loadingInvoices = false;
    });
  }

  private summariseInvoices(): void {
    this.invoiceSums = { sent_total_gross: 0, sent_total_vat: 0, unsent_total_gross: 0, unsent_total_vat: 0 };
    this.invoices.forEach((invoice) => {
      if (invoice.status === InvoiceStatus.sent) {
        this.invoiceSums.sent_total_gross += parseFloat(invoice.total_gross);
        this.invoiceSums.sent_total_vat += parseFloat(invoice.total_vat);
      } else if (invoice.status === InvoiceStatus.created) {
        this.invoiceSums.unsent_total_gross += parseFloat(invoice.total_gross);
        this.invoiceSums.unsent_total_vat += parseFloat(invoice.total_vat);
      }
    });
  }

  createManualInvoice(invoice: ManualInvoice): void {
    invoice.campaign_id = this.campaign.id;

    const dialogContent: ModalContent =
      this.invoiceType === InvoiceType.Credit
        ? {
            title: 'Skicka kreditfaktura',
            texts: [
              `Är du säker på att du vill kreditfakturera <strong>${this.campaign.name}</strong> med <strong>${invoice.total_gross_excluding_agency_discount}</strong> kr <strong>${invoice.invoice_date}</strong>?`,
            ],
            confirmLabel: 'Ja, fortsätt',
            cancelLabel: 'Nej, avbryt',
          }
        : {
            title: 'Skicka debetfaktura',
            texts: [
              `Är du säker på att du vill fakturera <strong>${this.campaign.name}</strong> med <strong>${invoice.total_gross_excluding_agency_discount}</strong> kr <strong>${invoice.invoice_date}</strong>?`,
            ],
            confirmLabel: 'Ja, fortsätt',
            cancelLabel: 'Nej, avbryt',
          };

    const valid = this.validateInvoicePlan(invoice.total_gross_excluding_agency_discount);
    if (!valid) {
      dialogContent.warnings = [
        {
          title: '',
          texts: ['Observera att faktureringsplanen inte är lika med kampanjens totala budget.'],
        },
      ];
    }

    this.modalService.openConfirm(dialogContent).then((response) => {
      if (!response.confirm) return;

      this.invoiceService
        .createManualInvoice(invoice)
        .then((invoice: Invoice) => {
          if (invoice.attest_status === AttestStatus.AwaitingAttest) {
            this.modalService.openSuccess(`Kreditfaktura är skapad!
          Ni måste skapa ett Scrive-underlag för att ekonomi ska kunna godkänna denna kreditfaktura.`);
          } else {
            this.modalService.openSuccess('Fakturan har skapats.');
          }
        })
        .catch(() => {
          const msg = 'Det gick inte att skapa en faktura.';
          this.modalService.openError(msg);
        })
        .finally(() => {
          this.closeInvoiceForm();
          this.reloadInvoices();
        });
    });
  }

  invoiceDisabledMessages(): string[] {
    return uniq(
      this.campaign.targetings
        .map((t) => (t.includes_linear ? 'Regionalpaketering' : t.invoice_disable_message))
        .filter((msg) => msg && msg.length),
    );
  }

  salesOrderNumbers(): string[] {
    return uniq(this.campaign.targetings.map((t) => t.sales_order_number).filter((so) => so && so.length));
  }

  paymentTerms(invoice: Invoice): string {
    return [invoice.payment_terms, invoice.payment_terms_code].filter((term) => term !== null).join(' - ');
  }

  showAttestStatus(invoice: Invoice): boolean {
    return invoice.attest_status !== AttestStatus.NoAttestNeeded && invoice.status !== 'cancelled';
  }

  translateAttestStatus(attestStatus: AttestStatus): string {
    return this.translationService.convertAttestStatus(attestStatus);
  }
}
